import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getLeadMagnets, registerDownload } from "@/api";
import { useSeo } from "@/seo";
import { Reveal, SectionHead, Eyebrow } from "@/components/Reveal";

export default function Resources() {
  const [magnets, setMagnets] = useState([]);
  const [active, setActive] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);

  useSeo({
    title: "Guides & Checklists, UAE TP, India GCC, Family Office, TruVerse",
    description: "Download the UAE Transfer Pricing Checklist, India GCC Setup Guide and Family Office in Dubai Starter Guide from TruVerse Middle East.",
  });

  useEffect(() => {
    getLeadMagnets().then(setMagnets).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!active) return;
    setSending(true);
    try {
      const res = await registerDownload(active.slug, { email, name });
      toast.success("Your download has started.");
      const url = res.file_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${res.file_url}` : res.file_url;
      window.open(url, "_blank", "noopener");
      setActive(null);
      setEmail("");
      setName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="resources-page">
      <section className="bg-ink py-24 lg:py-28">
        <div className="container-site max-w-4xl">
          <Reveal>
            <Eyebrow>Resources</Eyebrow>
            <h1 className="mt-6 text-4xl leading-tight tracking-tight md:text-6xl">Guides & checklists.</h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-muted md:text-lg">
              Practical, plain-language material from our three practices. Free with your email.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24">
        <div className="container-site grid gap-8 md:grid-cols-3">
          {magnets.map((m, i) => (
            <Reveal key={m.doc_id} delay={i * 0.08}>
              <div className="flex h-full flex-col border border-border bg-card p-8" data-testid={`magnet-card-${m.slug}`}>
                <FileDown size={26} className="text-accent" strokeWidth={1.5} />
                <h2 className="mt-5 text-2xl text-foreground">{m.title}</h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{m.description}</p>
                <Button
                  onClick={() => setActive(m)}
                  data-testid={`magnet-download-btn-${m.slug}`}
                  className="mt-6 rounded-sm bg-accent text-sm font-semibold text-accent-foreground hover:bg-accent/85"
                >
                  Download the guide
                </Button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent className="rounded-sm">
          <div data-testid="magnet-gate-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">{active?.title}</DialogTitle>
            <DialogDescription>Enter your email and the download starts immediately.</DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="magnet-gate-form">
            <Input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="magnet-name-input" className="rounded-sm" />
            <Input placeholder="Work email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="magnet-email-input" className="rounded-sm" />
            <Button type="submit" disabled={sending} data-testid="magnet-gate-submit-btn" className="w-full rounded-sm bg-accent text-accent-foreground hover:bg-accent/85">
              {sending ? "Preparing…" : "Get the guide"}
            </Button>
          </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
